/**
 * Weighted random pick — shared by every game mechanic in the catalog.
 *
 * Rating semantics (1–5):
 *   - rating 1 = common  → high weight  → frequent winner
 *   - rating 5 = rare    → low  weight  → rare      winner
 *   Formula: weight = (6 - rating)²
 *
 * Items with `available <= 0` are skipped. Returns -1 if no valid items.
 *
 * Centralizing this means: change the probability curve once and every game
 * (wheel, scratch, plinko, pirinola, slots, random-draw) inherits it.
 */
export interface WheelItem {
  id: number;
  name: string;
  rating: number;
  available: number;
  image?: string | null;
  logo?: string | null;
}

export function pickPrize(items: WheelItem[]): number {
  const weights = items.map((s) =>
    s.available <= 0 ? 0 : Math.pow(6 - Math.max(1, Math.min(5, s.rating)), 2),
  );
  const total = weights.reduce((a, b) => a + b, 0);
  if (total === 0) return -1;
  let r = Math.random() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

/** Common props every game component receives from the public wizard. */
export interface GamePlayProps {
  items: WheelItem[];
  primary: string;
  accent: string;
  /**
   * Active survey locale. Games that render their own copy (the ported
   * canvas mechanics) switch their EN/ES strings on this; prize names use
   * `WheelItem.nameTranslate` when present. Defaults to 'en'.
   */
  lang?: 'en' | 'es';
  /**
   * Brand logo URL (already resolved) — tiled on the game surface by the
   * mechanics that treat the brand as structural (scratch foil, pirinola
   * face, xinkit ball). Optional; games fall back to a wordmark/emoji.
   */
  logo?: string | null;
  /** Optional game-specific config from bs_event.customization.gameConfig. */
  config?: Record<string, unknown>;
  onWin: (item: WheelItem) => void;
}
